/**
 * TRACKING MAP - every Plausible event the assistant sends. Check here before
 * adding a button or link: most new UI fits an existing event + prop value,
 * and a new event name needs a goal in the dashboard.
 *
 * User actions (only explicit clicks - never hover, scroll or visibility):
 * - `form_submit` `question_asked_v2`: a question was sent (chat-page.tsx).
 * - `click_out`: every link that leaves the assistant, internal or external,
 *   split by `position`: citation_hovercard | sources_footer (sources),
 *   ruta_link (links in a tool box), answer_text (links the model wrote in the
 *   answer), menu (service menu). `is_internal` separates Internetmedicin.
 *   One event per click (each click is a navigation).
 * - `button_click` `copy`: every copy button, `target` = ruta | svar |
 *   kodblock. Rutor add `ruta` (koder/lathund/kalkylator), `part` (kod/text)
 *   and, for codes, `classification` (icd/kva - same values as the code search
 *   on verktyg.internetmedicin.se). Counted once per button while it is shown
 *   (per mount: a re-rendered answer can count again, rare in practice).
 * - `button_click` `ruta_expand` (`what`: visa_hela | details) and
 *   `download_answer`: once per element while it is shown (per mount).
 * - `button_click` misc labels: new chat, region, PWA prompt, Snabbsök
 *   dismiss/expand, embed "open in assistant" (see the call sites).
 * - `snabbsok_impression` / `snabbsok_click` / `snabbsok_rating`, `voice_input`,
 *   `ad_click`: own events, documented below.
 * - The navbar search dialog: `search_started` / `search` /
 *   `search_result_click` with `surface: 'assistent_modal'` - the same events
 *   as the ⌘K modal on internetmedicin.se (see trackSearchStarted below), plus
 *   `button_click` `open_search_modal`.
 *
 * Passive (interactive: false): `ad_impression`, `surface_view`,
 * `answer_error`.
 *
 * Deliberately NOT tracked: hover on citations (noisy), answer ratings (stored
 * in chat_ratings), "a tool box was shown" (count it from the answer log:
 * chat_answers holds the [[ruta:…]] markers, so no impression event needed),
 * middle-click / "open in new tab" via the context menu (only onClick is
 * listened to; links already open in a new tab), and relative links within
 * the assistant (they do not leave it).
 * Never send the question, the copied text or other free text.
 */
import { init, track } from '@plausible-analytics/tracker'
import { type AnswerErrorKind } from '#app/utils/answer-error.ts'

const SITE = 'assistent'

export const initPlausible = () => {
	try {
		init({
			domain: 'internetmedicin.se',
			endpoint: '/api/event',
		})
	} catch (error) {
		console.error('Error initiating Plausible', error)
	}
}

/**
 * Ad tracking events.
 *
 * ⚠️ PLAUSIBLE DASHBOARD SYNC REQUIRED
 * Adding or renaming event names here requires a matching update in the
 * Plausible dashboard under: Site Settings → Goals → Add goal (Custom event).
 * Without this, new events will not appear in the dashboard or Stats API.
 */
interface AdEvent {
	eventName: 'ad_click' | 'ad_impression'
	adId: string
}

export const trackAdEvent = ({ eventName, adId }: AdEvent) => {
	try {
		track(eventName, {
			interactive: eventName !== 'ad_impression',
			props: {
				adId,
				site: SITE,
			},
		})
	} catch (error) {
		console.error(
			'Error tracking Ad event with Plausible',
			{ eventName, adId, SITE },
			error,
		)
	}
}

/**
 * UI interaction events.
 *
 * ⚠️ PLAUSIBLE DASHBOARD SYNC REQUIRED
 * Adding or renaming event names here requires a matching update in the
 * Plausible dashboard under: Site Settings → Goals → Add goal (Custom event).
 * Without this, new events will not appear in the dashboard or Stats API.
 *
 * Note: prop values (label, location) are free-form and do NOT need dashboard
 * configuration — only the event name itself does. However, avoid renaming
 * existing label values as it permanently disconnects historical data.
 */
interface UIEvent {
	eventName: 'button_click' | 'link_click' | 'form_submit'
	label: string // what was clicked, e.g. 'subscribe', 'download_pdf'
	location?: string // where on the page, e.g. 'article_footer', 'header'
	/**
	 * Optional extra props merged into the event. Use for context-specific
	 * dimensions like `platform`, `browser`, `action`, `step`. Values must be
	 * strings (Plausible only accepts string custom prop values).
	 */
	extraProps?: Record<string, string>
}

export const trackUIEvent = ({
	eventName,
	label,
	location,
	extraProps,
}: UIEvent) => {
	try {
		track(eventName, {
			props: {
				label,
				...(location ? { location } : {}),
				...(extraProps ?? {}),
				site: SITE,
			},
		})
	} catch (error) {
		console.error(
			'Error tracking UI event with Plausible',
			{ eventName, label },
			error,
		)
	}
}

/**
 * Copy buttons - a `button_click` (existing goal, no dashboard change) with
 * `label: 'copy'`. The copied text is never sent, only which kind of button.
 * - `target`: ruta (a tool box) | svar (the whole answer, icon under it) |
 *   kodblock (a code block in the answer)
 * - rutor only: `ruta` (koder/lathund/kalkylator), `part` (kod = the code,
 *   text = its name) and, for codes, `classification` (icd/kva).
 * "Download as markdown" is not a copy: `download_answer` via trackUIEvent.
 */
export type CopyTarget = 'ruta' | 'svar' | 'kodblock'

export const trackCopy = ({
	target,
	location,
	extraProps,
}: {
	target: CopyTarget
	location: string
	/** For rutor: `ruta`, `part`, `classification`. */
	extraProps?: Record<string, string>
}) =>
	trackUIEvent({
		eventName: 'button_click',
		label: 'copy',
		location,
		extraProps: { target, ...(extraProps ?? {}) },
	})

/** `location` for chat events, same values as the other UI events. */
export const chatLocation = (variant: 'full' | 'embed') =>
	variant === 'embed' ? 'assistent_embed' : 'assistent_start_page'

/**
 * Source name and internal flag for any http(s) link, for click_out from
 * places without a source record (tool boxes, answer text, menu). Every
 * *.internetmedicin.se host counts as internal "Internetmedicin"; other hosts
 * are named by their hostname. Null for non-http links (mailto:, #anchors).
 */
export const linkTarget = (
	href: string,
): { source: string; isInternal: boolean } | null => {
	try {
		const url = new URL(href)
		if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
		const host = url.hostname.replace(/^www\./, '')
		const isInternal =
			host === 'internetmedicin.se' || host.endsWith('.internetmedicin.se')
		return { source: isInternal ? 'Internetmedicin' : host, isInternal }
	} catch {
		return null
	}
}

/** ICD-10 vs KVÅ from the code's format, for the `classification` prop. */
export const codeClassification = (code: string | undefined) =>
	!code
		? undefined
		: /^[A-Z]\d{2}/.test(code)
			? 'icd'
			: /^([A-Z]{3}\d{2}|[A-Z]{2}\d{3})/.test(code)
				? 'kva'
				: undefined

/**
 * Surface-view tracking — generic "this UI surface was shown" event.
 *
 * One goal in the dashboard (`surface_view`) covers all passive
 * "syntes"-events; the specific surface is carried in the `surface` prop
 * (e.g. `pwa_install_sheet`). Future surfaces (tutorial cards, banners,
 * onboarding overlays) reuse this helper with a new `surface` value rather
 * than registering a new goal per yta.
 *
 * `ad_impression` predates this convention and stays as a named exception
 * for historical-data continuity.
 *
 * ⚠️ PLAUSIBLE DASHBOARD SYNC REQUIRED
 * Add `surface_view` as a custom goal in the Plausible dashboard under
 * Site Settings → Goals → Add goal (Custom event). The `surface` prop is
 * free-form and does not need dashboard registration.
 *
 * `interactive: false` marks the event as passive so it does not count
 * toward bounce-rate calculations — same convention as `ad_impression`.
 */
interface SurfaceViewEvent {
	surface: string // e.g. 'pwa_install_sheet'; future surfaces add their own value
	/**
	 * Optional extra props merged into the event. Use for surface-specific
	 * context (`platform`, `browser`, `step`, etc.). Values must be strings
	 * (Plausible only accepts string custom prop values).
	 */
	extraProps?: Record<string, string>
}

export const trackSurfaceView = ({ surface, extraProps }: SurfaceViewEvent) => {
	try {
		track('surface_view', {
			interactive: false,
			props: {
				surface,
				...(extraProps ?? {}),
				site: SITE,
			},
		})
	} catch (error) {
		console.error(
			'Error tracking surface_view with Plausible',
			{ surface, extraProps },
			error,
		)
	}
}

/**
 * Click-out tracking — outbound clicks from citation hovercards and source
 * footers to third-party medical sources (FASS, 1177, Internetmedicin, …).
 *
 * ⚠️ PLAUSIBLE DASHBOARD SYNC REQUIRED
 * `click_out` is a new custom goal. Add it in Plausible under
 * Site Settings → Goals → Add goal (Custom event) named `click_out`.
 *
 * Props:
 * - `destination_source`: canonical source name from `sourceDisplayName()`
 *   (e.g. "Internetmedicin", "FASS"). Used for business reports — partners want
 *   their canonical name, not a hostname or a corpus code.
 * - `destination_host`: raw hostname (lowercased, `www.` stripped). Catches
 *   unknown sources that aren't in `SOURCE_NAMES` / `SOURCE_CODE_NAMES`.
 * - `destination_url`: full URL — answers "which specific page on fass.se?"
 * - `position`: where the click originated (hovercard vs footer).
 * - `is_internal`: stringified boolean (Plausible only accepts string props).
 * - `citation_number`: only present when position is citation_hovercard.
 * - `location`: caller-supplied page-level location ("assistent_start_page"
 *   or "assistent_embed"), matching the convention used elsewhere.
 */
interface ClickOutEvent {
	destinationUrl: string
	destinationSource: string
	position:
		| 'citation_hovercard'
		| 'sources_footer'
		| 'ruta_link'
		| 'answer_text'
		| 'menu'
	isInternal: boolean
	citationNumber?: number
	location?: string
	/** Context for the position, e.g. `ruta`, `part`, `classification`. */
	extraProps?: Record<string, string>
}

/**
 * Token count of a free-form query (whitespace split).
 */
export const wordCount = (query: string): number =>
	query.trim().split(/\s+/).filter(Boolean).length

/**
 * Bucket a free-form query's token count into a coarse band. Used by
 * `trackSnabbsokImpression` so we can see whether long natural-language queries
 * convert as well as keyword ones, WITHOUT sending the raw query - Snabbsök
 * queries are medical and high-cardinality, so the raw text never leaves the
 * client. Buckets match the design doc (1-2w / 3-5w / 6-10w / 11+w).
 */
export const queryLenBucket = (query: string): string => {
	const n = wordCount(query)
	if (n <= 2) return '1-2w'
	if (n <= 5) return '3-5w'
	if (n <= 10) return '6-10w'
	return '11+w'
}

/**
 * Bucket a 1-based result position into a coarse band so the dashboard can
 * trend click-distribution over time even as total_shown varies. Buckets match
 * the design doc (1 / 2 / 3 / 4-5 / 6+).
 */
export const positionBucket = (position: number): string => {
	if (position <= 1) return '1'
	if (position === 2) return '2'
	if (position === 3) return '3'
	if (position <= 5) return '4-5'
	return '6+'
}

export const trackClickOut = ({
	destinationUrl,
	destinationSource,
	position,
	isInternal,
	citationNumber,
	location,
	extraProps,
}: ClickOutEvent) => {
	try {
		// Strip query string + fragment before sending to Plausible. Query params
		// on third-party source URLs may include identifying or session-y values
		// (`?userType=2&nplId=123`, login redirects, etc.) — they're not useful
		// for partner-traffic reporting and risk landing in our analytics. The
		// origin + pathname is enough to attribute clicks per source URL.
		// Plausible custom-prop values also have a length limit; long query
		// strings would otherwise silently truncate.
		const parsed = new URL(destinationUrl)
		const host = parsed.hostname.replace(/^www\./, '')
		const pathOnly = `${parsed.origin}${parsed.pathname}`
		track('click_out', {
			props: {
				destination_source: destinationSource,
				destination_host: host,
				destination_url: pathOnly,
				position,
				is_internal: String(isInternal),
				...(citationNumber !== undefined
					? { citation_number: String(citationNumber) }
					: {}),
				...(location ? { location } : {}),
				...(extraProps ?? {}),
				site: SITE,
			},
		})
	} catch (error) {
		console.error(
			'Error tracking click_out with Plausible',
			{ destinationUrl, position },
			error,
		)
	}
}

/**
 * Snabbsök tracking - impression (results first render for a query) and click
 * (any title/hover/sitelink anchor).
 *
 * ⚠️ PLAUSIBLE DASHBOARD SYNC REQUIRED
 * `snabbsok_impression` and `snabbsok_click` are new custom goals. Add both
 * in the Plausible dashboard under Site Settings → Goals → Add goal (Custom
 * event) before merging, same caveat as `click_out`.
 *
 * Privacy: we deliberately do NOT send the raw query - Snabbsök queries are
 * medical and high-cardinality, so the impression event carries only a coarse
 * `query_len_bucket` (1-2w / 3-5w / 6-10w / 11+w via `queryLenBucket`). The
 * click event carries no query at all; engagement = click-visitors /
 * impression-visitors per query-length band, joined in the dashboard.
 *
 * Click URLs are stripped to origin + pathname (same as `trackClickOut`) - the
 * indexed sources sometimes carry session-y query params that aren't useful
 * for ranking-quality reporting.
 */
interface SnabbsokImpressionEvent {
	/**
	 * Caller-bucketed via `queryLenBucket` BEFORE calling - keeps the privacy
	 * boundary visible at the call site (the raw query never reaches this
	 * helper).
	 */
	queryLenBucket: string
	imShown: number
	extShown: number
	region: string
	/**
	 * UUID v4 minted client-side per fetched Snabbsök query, shared with the
	 * Postgres search-feedback store. Carried here as a Plausible custom prop
	 * so the two stores share a join key (Plausible product metrics ⟷ Postgres
	 * training data). Plausible handles high-cardinality custom-prop values
	 * gracefully - they store as filterable values, not enumerated dimensions,
	 * so a UUID per impression is fine. Omit when the feedback store is not
	 * wired (e.g. dev/dark deploy).
	 */
	impressionId?: string
	location?: string
}

export const trackSnabbsokImpression = ({
	queryLenBucket,
	imShown,
	extShown,
	region,
	impressionId,
	location,
}: SnabbsokImpressionEvent) => {
	try {
		track('snabbsok_impression', {
			interactive: false,
			props: {
				query_len_bucket: queryLenBucket,
				im_shown: String(imShown),
				ext_shown: String(extShown),
				total_shown: String(imShown + extShown),
				region,
				...(impressionId ? { impression_id: impressionId } : {}),
				...(location ? { location } : {}),
				site: SITE,
			},
		})
	} catch (error) {
		console.error(
			'Error tracking snabbsok_impression with Plausible',
			{ queryLenBucket, region },
			error,
		)
	}
}

interface SnabbsokClickEvent {
	destinationUrl: string
	destinationSource: string
	sourceType: 'im' | 'external'
	/** 1-based position within the visible list at click-time. */
	position: number
	/** Total rows visible across both sections when the click fired. */
	totalShown: number
	region: string
	isSitelink: boolean
	/** 0-based index of the sitelink under its parent, when `isSitelink`.
	 *  By design: the matching feedback-DB beacon uses 1-based (snabbsok-
	 *  click.ts zod 1..20), so cross-store joins on sitelink_index need
	 *  `plausible.sitelink_index + 1 = postgres.sitelink_index`. See
	 *  snabb-sok.tsx for the parallel call-site annotation. (PR #37 review.) */
	sitelinkIndex?: number
	/**
	 * UUID v4 of the impression this click belongs to - same value that was
	 * sent on the matching `snabbsok_impression` event AND stored as the
	 * Postgres feedback row's `impression_id`. Lets us join Plausible-side
	 * aggregations (per-card click-through-rate) and SQL-side training data
	 * by the same key. Omit during the early-mount window before the
	 * impression has been minted; click rows with no impression_id still
	 * land but won't join cleanly to a card.
	 */
	impressionId?: string
	location?: string
}

export const trackSnabbsokClick = ({
	destinationUrl,
	destinationSource,
	sourceType,
	position,
	totalShown,
	region,
	isSitelink,
	sitelinkIndex,
	impressionId,
	location,
}: SnabbsokClickEvent) => {
	try {
		// Same URL hygiene as trackClickOut — origin + pathname only. Plausible's
		// prop length limit truncates long URLs silently, and indexed source URLs
		// sometimes carry session params we don't want in analytics.
		// (NB: the Postgres feedback store keeps the FULL URL including fragment
		// for #section anchor signal — different precision needs, different
		// sinks. snabb-sok.tsx fires both on each click.)
		const parsed = new URL(destinationUrl)
		const host = parsed.hostname.replace(/^www\./, '')
		const pathOnly = `${parsed.origin}${parsed.pathname}`
		track('snabbsok_click', {
			props: {
				destination_source: destinationSource,
				destination_host: host,
				destination_url: pathOnly,
				source_type: sourceType,
				position: String(position),
				position_bucket: positionBucket(position),
				total_shown: String(totalShown),
				region,
				is_sitelink: String(isSitelink),
				...(isSitelink && sitelinkIndex !== undefined
					? { sitelink_index: String(sitelinkIndex) }
					: {}),
				...(impressionId ? { impression_id: impressionId } : {}),
				...(location ? { location } : {}),
				site: SITE,
			},
		})
	} catch (error) {
		console.error(
			'Error tracking snabbsok_click with Plausible',
			{ destinationUrl, position },
			error,
		)
	}
}

/**
 * Snabbsök rating — thumbs up/down on search-result quality.
 *
 * Reuses the existing `button_click` goal (NO new dashboard goal needed) with
 * label `snabbsok_rating_positive` / `snabbsok_rating_negative`, same convention
 * as `snabbsok_dismissed`. The impression id is carried as a custom prop so the
 * rating joins to its impression (and to the Postgres rating row) in the
 * dashboard. The free-text comment is NEVER sent to Plausible — it lives only in
 * Postgres (same privacy boundary as the raw query).
 */
interface SnabbsokRatingEvent {
	rating: 'positive' | 'negative'
	impressionId?: string
	region: string
	location?: string
}

export const trackSnabbsokRating = ({
	rating,
	impressionId,
	region,
	location,
}: SnabbsokRatingEvent) => {
	try {
		track('button_click', {
			props: {
				label: `snabbsok_rating_${rating}`,
				region,
				...(impressionId ? { impression_id: impressionId } : {}),
				...(location ? { location } : {}),
				site: SITE,
			},
		})
	} catch (error) {
		console.error(
			'Error tracking snabbsok_rating with Plausible',
			{ rating, region },
			error,
		)
	}
}

/**
 * Failed/incomplete answer in the chat - one event per run, fired by
 * chat-page.tsx on thread.run-end when the server sent the `answerError` data
 * part. `kind` is a closed enum (answer-error.ts); no question and no error
 * message leaves the client.
 *
 * ⚠️ PLAUSIBLE DASHBOARD SYNC REQUIRED
 * Add `answer_error` as a custom goal (Site Settings → Goals → Add goal →
 * Custom event). Props `kind`, `engine`, `location` need no dashboard
 * registration; their VALUES are closed sets (see AnswerErrorEvent below).
 *
 * `interactive: false`: the error is not a user click (same convention as
 * surface_view / snabbsok_impression).
 */
interface AnswerErrorEvent {
	kind: AnswerErrorKind
	/** 'docsbot' is historical only (IM-61): it exists in Plausible's history. */
	engine: 'selfbuilt' | 'docsbot'
	location: 'assistent_start_page' | 'assistent_embed'
}

export const trackAnswerError = ({
	kind,
	engine,
	location,
}: AnswerErrorEvent) => {
	try {
		track('answer_error', {
			interactive: false,
			props: { kind, engine, location, site: SITE },
		})
	} catch (error) {
		console.error(
			'Error tracking answer_error with Plausible',
			{ kind, engine },
			error,
		)
	}
}

/**
 * Voice input - one event per finished recording (the microphone button,
 * voice-input-button.tsx). Neither audio nor text leaves the client here, only
 * the outcome.
 *
 * ⚠️ PLAUSIBLE DASHBOARD SYNC REQUIRED
 * Add `voice_input` as a custom goal (Site Settings → Goals → Add goal →
 * Custom event). Props need no registration; their VALUES are closed sets:
 * - `outcome`: transcribed = text in the field; empty = the server heard no
 *   speech (e.g. filtered echo); no_speech = the browser heard no speech, no
 *   request; cancelled = ✕/Esc; discarded = the transcript arrived after the
 *   question was already sent and was dropped; denied = microphone permission
 *   denied; error = server/network error; abandoned = the page was left or the
 *   button unmounted during the permission prompt or the recording.
 * - `mode`: tap (tap/keyboard) | hold (hold while speaking). Only once the
 *   recording started - on a denial the finger is often still down.
 * - `duration_bucket`: recording length (0-3s / 3-6s / 6-10s / 10-20s);
 *   only once the recording started.
 * - `attempt` (1 / 2 / 3+) and `after_help` (yes / no: the permission help
 *   text was shown on an earlier attempt), per page load.
 * - Only for `abandoned`: `stage` (prompt / recording).
 * - Only for `denied` (raw signals, voice-input-diagnostics.ts): `error_name`,
 *   `error_detail` (Chromium's message class), `perm_before` / `perm_after`
 *   (granted / prompt / denied / unknown), `wait_bucket` (<50ms / 50-250ms /
 *   250ms-1s / 1-5s / >5s) and `help` (shown / none).
 * Names and values are IDENTICAL to the site's voice_input (IM-128), so one
 * query covers both; `site` tells them apart.
 * - Only for `transcribed`: `query_len_bucket` (the SAME bands as
 *   snabbsok_impression, so spoken and typed questions can be compared) and
 *   `word_count` (exact word count, for the distribution within 11+w).
 *   Computed by the caller - the transcript never reaches this helper.
 *
 * How the question was then sent (unchanged/edited) is carried by
 * `input_method` on `question_asked` (chat-page.tsx), not here.
 */
export type VoiceInputOutcome =
	| 'transcribed'
	| 'empty'
	| 'no_speech'
	| 'cancelled'
	| 'discarded'
	| 'denied'
	| 'error'
	| 'abandoned'

/** Attempt, help text and failure signals for `voice_input` (IM-128). */
export type VoiceInputDetails = {
	/** Attempt number within the page load. */
	attempt?: '1' | '2' | '3+'
	/** The permission help text was shown on an earlier attempt. */
	afterHelp?: boolean
	/** For `abandoned`: where in the flow it ended. */
	stage?: 'prompt' | 'recording'
	/** For `denied`: the raw signals from voice-input-diagnostics. */
	failure?: {
		errorName: string
		errorDetail: string
		permBefore: string
		permAfter: string
		waitBucket: string
		helpShown: boolean
	}
}

/** Recording length in coarse bands; the upper bound is the 20 s maximum. */
export const durationBucket = (ms: number): string => {
	if (ms < 3000) return '0-3s'
	if (ms < 6000) return '3-6s'
	if (ms < 10000) return '6-10s'
	return '10-20s'
}

export const trackVoiceInput = ({
	outcome,
	mode,
	location,
	durationBucket,
	queryLenBucket,
	wordCount,
	attempt,
	afterHelp,
	stage,
	failure,
}: {
	outcome: VoiceInputOutcome
	mode?: 'tap' | 'hold'
	location: 'assistent_start_page'
	durationBucket?: string
	queryLenBucket?: string
	wordCount?: number
} & VoiceInputDetails) => {
	try {
		track('voice_input', {
			props: {
				outcome,
				...(mode ? { mode } : {}),
				location,
				...(attempt ? { attempt } : {}),
				...(afterHelp !== undefined
					? { after_help: afterHelp ? 'yes' : 'no' }
					: {}),
				...(stage ? { stage } : {}),
				...(failure
					? {
							error_name: failure.errorName,
							error_detail: failure.errorDetail,
							perm_before: failure.permBefore,
							perm_after: failure.permAfter,
							wait_bucket: failure.waitBucket,
							help: failure.helpShown ? 'shown' : 'none',
						}
					: {}),
				...(durationBucket ? { duration_bucket: durationBucket } : {}),
				...(queryLenBucket ? { query_len_bucket: queryLenBucket } : {}),
				...(wordCount !== undefined ? { word_count: String(wordCount) } : {}),
				site: SITE,
			},
		})
	} catch (error) {
		console.error(
			'Error tracking voice_input with Plausible',
			{ outcome },
			error,
		)
	}
}

/**
 * The navbar search dialog - the SAME three events as the ⌘K modal on im.se
 * (`search_started`, `search`, `search_result_click`) with the same props, so
 * the two modals compare directly in the dashboard. `site: 'assistent'` +
 * `surface: 'assistent_modal'` tell them apart. All three already exist as
 * goals (the site created them), so no dashboard change is needed.
 *
 * ⚠️ `search_started` is one half of the search-volume KPI (search_started +
 * question_asked). From this surface on, assistant navbar searches count
 * there too - compare per `site`, or filter out `site=assistent` for a
 * series comparable with the past. The same value is the `location` of the
 * dialog's rows in snabbsok_impressions. The raw query is never sent, only
 * length buckets and counts.
 */
export const SEARCH_MODAL_SURFACE = 'assistent_modal'

/** `search_started` - once per started search (passive, as on the site). */
export const trackSearchStarted = ({ region }: { region: string }) => {
	try {
		track('search_started', {
			interactive: false,
			props: {
				surface: SEARCH_MODAL_SURFACE,
				region,
				input_method: 'text',
				site: SITE,
			},
		})
	} catch (error) {
		console.error('Error tracking search_started with Plausible', error)
	}
}

/** Shortest query that counts - the engine's own floor (shorter is never searched). */
export const MIN_TRACKED_QUERY_LENGTH = 2

export const shouldRecordSearch = (query: string | undefined): boolean =>
	(query ?? '').trim().length >= MIN_TRACKED_QUERY_LENGTH

/** `search` - once per finished query (passive impression). */
export const trackSearchSettled = ({
	query,
	records,
	region,
}: {
	query: string
	records: ReadonlyArray<{ isExternal?: boolean }>
	region: string
}) => {
	try {
		const extShown = records.filter(r => r.isExternal).length
		track('search', {
			interactive: false,
			props: {
				query_len_bucket: queryLenBucket(query),
				im_shown: String(records.length - extShown),
				ext_shown: String(extShown),
				total_shown: String(records.length),
				has_results: String(records.length > 0),
				region,
				surface: SEARCH_MODAL_SURFACE,
				input_method: 'text',
				site: SITE,
			},
		})
	} catch (error) {
		console.error('Error tracking search with Plausible', error)
	}
}

/**
 * `search_result_click` - a click on a result row (not a sitelink, as on the
 * site). No destination url: internal PM addresses reveal the topic.
 */
export const trackSearchResultClick = ({
	source,
	isExternal,
	position,
	totalShown,
	region,
}: {
	source: string
	isExternal: boolean
	position: number
	totalShown: number
	region: string
}) => {
	try {
		track('search_result_click', {
			props: {
				destination_source: source,
				source_type: isExternal ? 'external' : 'im',
				position: String(position),
				position_bucket: positionBucket(position),
				total_shown: String(totalShown),
				region,
				surface: SEARCH_MODAL_SURFACE,
				input_method: 'text',
				site: SITE,
			},
		})
	} catch (error) {
		console.error('Error tracking search_result_click with Plausible', error)
	}
}
